import { getCountries } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import type {
  BuildIntent,
  ExpectedVolume,
  Industry,
  JobRole,
  UseCase,
} from '../../../types/onboarding';

// Form state for the survey. Empty string / empty array = unanswered.
export interface OnboardingSurveyFormValues {
  job_role: JobRole | '';
  primary_use_case: UseCase[];
  primary_use_case_other: string;
  build_intent: BuildIntent | '';
  expected_volume: ExpectedVolume | '';
  industry: Industry | '';
  country: string;
}

export type SurveyFieldType =
  | 'pills'
  | 'pills-multi'
  | 'cards'
  | 'segmented'
  | 'select';

export type SurveyOptionIcon =
  | 'dashboard'
  | 'api'
  | 'both'
  | 'help'
  | 'ticket'
  | 'card'
  | 'id'
  | 'key'
  | 'plane'
  | 'tag'
  | 'dots';

export interface SurveyOption {
  value: string;
  label: string;
  sublabel?: string;
  icon?: SurveyOptionIcon;
}

export interface SurveyQuestion {
  id: keyof OnboardingSurveyFormValues;
  prompt: string;
  helper?: string;
  note?: string;
  type: SurveyFieldType;
  options?: SurveyOption[];
  dynamicOptions?: 'country';
  otherText?: {
    key: keyof OnboardingSurveyFormValues;
    showWhen: string;
    placeholder: string;
  };
}

export const getCountryOptions = (): SurveyOption[] =>
  getCountries()
    .map((code) => ({
      value: code,
      label: (en as Record<string, string>)[code] || code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const EMPTY_SURVEY_VALUES: OnboardingSurveyFormValues = {
  job_role: '',
  primary_use_case: [],
  primary_use_case_other: '',
  build_intent: '',
  expected_volume: '',
  industry: '',
  country: '',
};

export const ONBOARDING_SURVEY_QUESTIONS: SurveyQuestion[] = [
  {
    id: 'job_role',
    prompt: 'Your role',
    type: 'pills',
    options: [
      { value: 'developer', label: 'Developer' },
      { value: 'product', label: 'Product' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'operations', label: 'Operations' },
      { value: 'founder', label: 'Founder' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    id: 'industry',
    prompt: 'What industry is your company in?',
    type: 'select',
    options: [
      { value: 'sport', label: 'Sport' },
      { value: 'hospitality', label: 'Hospitality' },
      { value: 'retail', label: 'Retail' },
      { value: 'events_entertainment', label: 'Events and entertainment' },
      { value: 'tourism_attractions', label: 'Tourism and attractions' },
      { value: 'transport_aviation', label: 'Transport and aviation' },
      { value: 'food_beverage', label: 'Food and beverage' },
      { value: 'technology', label: 'Technology' },
      { value: 'property', label: 'Property' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    id: 'primary_use_case',
    prompt: 'What type of passes do you want to create?',
    helper: 'select all that apply',
    type: 'pills-multi',
    otherText: {
      key: 'primary_use_case_other',
      showWhen: 'other',
      placeholder: 'Tell us what you’d like to create',
    },
    options: [
      { value: 'event_tickets', label: 'Event tickets', icon: 'ticket' },
      { value: 'loyalty_cards', label: 'Loyalty cards', icon: 'card' },
      { value: 'membership_passes', label: 'Membership passes', icon: 'id' },
      { value: 'access_passes', label: 'Access passes', icon: 'key' },
      { value: 'boarding_passes', label: 'Boarding passes', icon: 'plane' },
      { value: 'coupons', label: 'Coupons', icon: 'tag' },
      { value: 'other', label: 'Other', icon: 'dots' },
    ],
  },
  {
    id: 'expected_volume',
    prompt: 'Roughly how many passes will you issue in a year?',
    type: 'segmented',
    options: [
      { value: 'under_1k', label: 'Under 1k' },
      { value: '1k_10k', label: '1k–10k' },
      { value: '10k_100k', label: '10k–100k' },
      { value: '100k_1m', label: '100k–1m' },
      { value: '1m_plus', label: '1m+' },
    ],
  },
  {
    id: 'build_intent',
    prompt: 'How will you build with us?',
    type: 'cards',
    options: [
      {
        value: 'dashboard',
        label: 'Dashboard',
        sublabel: 'No code',
        icon: 'dashboard',
      },
      { value: 'api', label: 'API', sublabel: 'Integrate it', icon: 'api' },
      { value: 'both', label: 'Both', icon: 'both' },
      { value: 'not_sure', label: 'Not sure yet', icon: 'help' },
    ],
  },
  {
    id: 'country',
    prompt: 'Country',
    type: 'select',
    dynamicOptions: 'country',
  },
];

// Wizard steps: the questions grouped two per step (3 steps total).
const chunk = <T>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export const ONBOARDING_SURVEY_STEPS: SurveyQuestion[][] = chunk(
  ONBOARDING_SURVEY_QUESTIONS,
  2
);

// Invitees (non-lead users) answer only the account-level role question.
export const INVITEE_SURVEY_STEPS: SurveyQuestion[][] = [
  ONBOARDING_SURVEY_QUESTIONS.filter((q) => q.id === 'job_role'),
];

// Steps shown for a given user: leads get the full survey, invitees get role only.
export const getSurveySteps = (isLead: boolean): SurveyQuestion[][] =>
  isLead ? ONBOARDING_SURVEY_STEPS : INVITEE_SURVEY_STEPS;

// A step is answered when it has a non-empty value (multi needs ≥ 1).
export const isQuestionAnswered = (
  question: SurveyQuestion,
  value: OnboardingSurveyFormValues[keyof OnboardingSurveyFormValues]
): boolean => {
  if (question.type === 'pills-multi') {
    return Array.isArray(value) && value.length > 0;
  }
  return typeof value === 'string' && value.trim().length > 0;
};

export const MAX_OTHER_TEXT_LENGTH = 255;

export const isQuestionSatisfied = (
  question: SurveyQuestion,
  values: OnboardingSurveyFormValues
): boolean => {
  if (!isQuestionAnswered(question, values[question.id])) return false;

  const { otherText } = question;
  if (otherText) {
    const answer = values[question.id];
    const otherSelected =
      Array.isArray(answer) && answer.includes(otherText.showWhen);
    if (otherSelected) {
      const text = values[otherText.key];
      const length = typeof text === 'string' ? text.trim().length : 0;
      if (length < 1 || length > MAX_OTHER_TEXT_LENGTH) return false;
    }
  }
  return true;
};
